import { recordsForLink, temporalIdentity } from './temporalGraph';
import { nodeSummary } from './nodeSummary';
import { studyCohortIndex } from './studyCohortGraph';
const list = value => Array.isArray(value) ? value : typeof value === 'string' ? value.split(';').filter(Boolean) : [];
const endpoint = value => typeof value === 'object' ? value.id : value;
const unique = values => [...new Set(values.filter(Boolean))];
const parse = value => { try { return typeof value === 'string' ? JSON.parse(value) : value || []; } catch { return []; } };

export function decorateNodeAttributes(data, provenance) {
  const index = studyCohortIndex(provenance);
  const raw = index.byTitle;
  const namesById = new Map((provenance?.entities || []).map(entity => [entity.entity_id, entity.display_name || entity.name]));
  const nodes = data.nodes.map(node => {
    const entity = raw.get(node.id) || {};
    const aliases = unique([...(node.aliases || []), ...parse(entity.aliases),
      ...parse(entity.source_mentions).map(mention => mention.original_name)]).filter(alias => alias.toLowerCase() !== (node.name || node.id).toLowerCase());
    const isStudy = node.type === 'Study';
    const isCohort = node.type === 'Cohort';
    const cohorts = isStudy ? index.cohortsForStudy.get(entity.entity_id) || [] : unique([
      index.cohortNames.get((node.study_name || '').toLowerCase())?.display_name
        || index.cohortNames.get((node.study_name || '').toLowerCase())?.name,
      ...list(node.sources).flatMap(source => index.sourceCohorts.get(source) || []),
      ...(isCohort ? [entity.display_name || entity.name || node.name] : []),
    ]);
    const studies = isCohort ? index.studiesForCohort.get(entity.entity_id) || [] : unique([
      ...list(node.sources).flatMap(source => index.sourceStudies.get(source) || []),
      ...(isStudy ? [entity.display_name || entity.name || node.name] : []),
    ]);
    const age = temporalIdentity(node)?.timepoint;
    const interval = node.event_time?.start && node.event_time?.end ? `${node.event_time.start}–${node.event_time.end}` : null;
    const generated = !!entity.schema_version;
    const timepoints = generated ? list(entity.attribute_timepoints) : unique([age, interval]);
    return { ...node, reference_only: generated ? entity.reference_only : node.reference_only, aliases, description_summary: nodeSummary(node, entity), cohorts: generated ? list(entity.cohorts) : cohorts, studies: generated ? list(entity.studies) : studies, study_cohorts: generated ? list(entity.cohorts) : cohorts, measurement_timepoints: timepoints, attribute_timepoints: timepoints };
  });
  const byId = new Map(nodes.map(node => [node.id, node]));
  const links = data.links.map(link => {
    const records = recordsForLink(link, null);
    const source = byId.get(endpoint(link.source));
    const target = byId.get(endpoint(link.target));
    return { ...link, attributeRecords: records.map(record => ({ record,
      timepoints: record.source_timepoints || record.target_timepoints ? unique([...(record.source_timepoints || []), ...(record.target_timepoints || [])].map(point => point.label)) : unique([...(source?.attribute_timepoints || []), ...(target?.attribute_timepoints || [])]),
      cohorts: record.cohort_ids ? record.cohort_ids.map(id => namesById.get(id)).filter(Boolean) : index.sourceCohorts.get(record.source_file) || [],
      studies: record.study_ids ? record.study_ids.map(id => namesById.get(id)).filter(Boolean) : index.sourceStudies.get(record.source_file) || [],
    })) };
  });
  return { nodes, links };
}

export function filterNodeAttributes(data, filters) {
  if (!filters.timepoint && !filters.study && !filters.cohort) return data;
  const matches = entry => (!filters.timepoint || entry.timepoints.includes(filters.timepoint))
    && (!filters.study || entry.studies.includes(filters.study))
    && (!filters.cohort || entry.cohorts.includes(filters.cohort));
  const byId = new Map(data.nodes.map(node => [node.id, node]));
  const links = data.links.flatMap(link => {
    const records = (link.attributeRecords || []).filter(matches);
    if (link.attributeRecords?.length) {
      return records.length ? [{ ...link, attributeRecords: records,
        displayRecords: records.map(entry => entry.record),
        ...(link.statisticalRecords ? { statisticalRecords: records.map(entry => entry.record) } : {}),
        description: records.map(entry => JSON.stringify(entry.record)).join('\n'), weight: records.length }] : [];
    }
    const ends = [byId.get(endpoint(link.source)), byId.get(endpoint(link.target))];
    return matches({ timepoints: ends.flatMap(node => node?.attribute_timepoints || []), studies: ends.flatMap(node => node?.studies || []), cohorts: ends.flatMap(node => node?.cohorts || []) }) ? [link] : [];
  });
  const ids = new Set(links.flatMap(link => [endpoint(link.source), endpoint(link.target)]));
  return { nodes: data.nodes.filter(node => ids.has(node.id) || matches({ timepoints: node.attribute_timepoints || [], studies: node.studies || [], cohorts: node.cohorts || [] })), links };
}
